export default {
  activity_consumption_01: {
    title: 'Jeg reparerte noe i dag',
    description:
      'Kan logges på dager du reparerer klær eller andre ødelagte gjenstander i stedet for å kaste dem.',
    co2_explanation: `MARKDOWN:
## Fakta
- En av de mest effektive måtene å bidra til lavere klimagassutslipp fra eget forbruk på er å velge holdbare kvalitetsprodukter som kan fikses hvis de blir ødelagt. Økt levetid på klær, ting og utstyr vi allerede eier, gjør at vi ikke trenger å erstatte dem så ofte, og bidrar til mindre klimagassutslipp, søppel og forurensning. 
- Å være en ansvarlig forbruker betyr å redusere eget forbruk, gjennom reparasjon, gjenbruk og videresalg av ting du ikke lenger trenger. Du vil spare penger, og bidra til at vi utnytter ressursene våre bedre.

## Hva vi antar
- Beregningene for en husholdning er basert på makro-økonomisk input-output data, som gir beregninger for utslipp per krone brukt på ulike tjenester og produkter i ditt land.
- Det antas at om du reparerer produktene dine så varer de 20% lenger enn gjennomsnittet, og du har dermed 20% lavere miljøbelastning per krone du bruker på kjøp.
- Beregningene er basert på antagelsen om at du lever i henhold til denne vanen dersom du registrer den hver tredje dag i denne klimautfordringen.

## Tilleggsinformasjon
- [https://wwd.com/business-news/technology/think-tank-fashion-circular-economy-1202966172/](https://wwd.com/business-news/technology/think-tank-fashion-circular-economy-1202966172/)`,
    health_explanation: `MARKDOWN:
## Fakta
- I følge forskning er behovet for materielle eiendeler i større grad relatert til å være ulykkelig enn lykkelig. Et lavere forbruk kan hjelpe deg å investere mer tid i positive, ikke-monetære aspekter i livet som lykke, fritid, menneskelig godhet og tilknytning.
- Å endre måten vi tenker forbruk på og heller bruke tid og innsats på aktiviteter som vi selv syntes er meningsfulle kan føre til et liv med mer mening og kan gjøre deg lykkeligere.

## Tilleggsinformasjon
- [https://www.psychologytoday.com/us/blog/buddhist-economics/201803/why-over-consumption-is-making-us-unhappy](https://www.psychologytoday.com/us/blog/buddhist-economics/201803/why-over-consumption-is-making-us-unhappy)`,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_consumption_02: {
    title: 'Jeg har brukt penger på tjenester i stedet for varer i dag',
    description:
      'Bruk penger på teater, kino, nyheter, restauranter, behandlinger, produktutleie eller andre tjenester du ellers ville brukt på produkter.',
    co2_explanation: `MARKDOWN:
## Fakta
- Omtrent halvparten av tekstilene til klær er laget av syntetiske fibre, der råolje er det viktigste råmateriale. Disse tekstilene er ikke fornybare, og skader det marine dyrelivet med utslipp av mikroplast. Produksjon av naturlige klesfibre som bomull og ull beslaglegger også store landområder, og innebærer bruk av sprøytemidler og antibiotika. Velger du tjenester framfor produkter bidrar du til biologisk mangfold, dyrevelferd og at flere får tilgang til rent vann. I tillegg reduserer du utslipp av klimagasser betydelig.

## Hva vi antar
- I beregningene antas det at du er en del av en gjennomsnittlig husholdning med en gjennomsnittlig utgiftsprofil hentet fra nasjonale forbruksundersøkelser.
- Denne aktiviteten antar du alt av penger brukes på en eller annen måte, men det er store forskjeller i utslipp avhengig av hva du bruker pengene dine på. Eksempelvis har klær et utslipp på omtrent 51 gram CO₂e per krone, mens et restaurantbesøk har et utslipp på 15 gram CO₂e per krone.
- Beregningene er basert på at du som tjenesteforbruker, bruker 20% mindre penger på produkter enn gjennomsnittet og at disse pengene brukes på tjenester i stedet.
- Beregningene har lagt til grunn at denne aktiviteten skjer hver tredje dag. Registrer du denne aktiviteten oftere betyr det at du i realiteten har en høyere besparelse, og omvendt.

## Tilleggsinformasjon
- [https://superegoworld.com/blogs/the-world/the-dangers-of-synthetic-fibers-and-fabrics-on-the-environment](https://superegoworld.com/blogs/the-world/the-dangers-of-synthetic-fibers-and-fabrics-on-the-environment)`,
    health_explanation: `MARKDOWN:
## Fakta
- Tjenester ofte kan nytes sammen med andre, og meningsfulle opplevelser med de du bryr deg om kan bidra til egen lykke.
- Å endre måten vi tenker forbruk på og heller bruke ressurser på tjenester framfor produkter kan bidra til bedre helse og velvære fordi du knytter sterkere bånd til de du bryr deg om.
`,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_consumption_03: {
    title: 'Jeg donerte penger til et godt formål i dag',
    description:
      'Kan logges på dager hvor du donerer minst 25 NOK til en hvilken som helst god sak. Hvis du for eksempel allerede donerer 250 kr i måneden til en god sak kan du logge denne aktiviteten på 10 av dagene.',
    co2_explanation: `MARKDOWN:
## Fakta
- Å bruke penger uten å øke klimagassutslipp er så og si umulig, med mindre du bevisst investerer i noe som reduserer utslippene. Donasjoner er en enkel måte å støtte en god sak du bryr deg om, enten det er fattigdom, ulikhet, natur eller sosiale initiativer. Samtidig reduserer du sjansen for å bruke pengene på personlige varer og tjenester. Gir du bort penger reduserer du fotavtrykket ditt på en enkel og rask måte.

## Hva vi antar
- Besparelsen beregnes ved å finne utslippet for hva en gjennomsnittsperson i ditt land ville brukt dette beløpet på. Ved å donere pengene sparer du alle disse utslippene, og det er ikke lenger en del av fotavtrykket ditt, men bidrar til å støtte en god sak.
`,
    health_explanation: `MARKDOWN:
## Fakta
- Donasjoner kan øke lykkefølelsen, velvære og tilfredshet i livet på grunn av ditt sosiale ansvar ovenfor andre.
`,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_consumption_04: {
    title: 'Jeg resirkulerte mest mulig avfall i dag',
    description:
      'Kan logges på dager du sorterer og resirkulerer alt avfall som er resirkulerbart.',
    co2_explanation: `MARKDOWN:
## Fakta
- Gjenbruk er en av de mest effektive måtene å redusere vårt behov for råvarer, forbruke mindre energi og naturressurser sammenlignet med å bruke et helt nytt råstoff.
- Gjenvinning avgir skadelige kjemikalier og klimagasser som frigjøres fra deponier.

## Hva vi antar
- Denne aktiviteten antar at gjenvinningsgraden din øker til 80%.
`,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_consumption_05: {
    title:
      'Hver gang jeg virkelig må kjøpe et nytt produkt vil jeg velge et av høyest mulig kvalitet',
    description:
      'Du kan registrere denne aktiviteten hver dag så lenge du har bestemt deg for å velge produkter av høyeste kvalitet neste gang du må kjøpe noe nytt. Det vil si at det bør være enkelt å reparere og vedlikeholde.',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_consumption_06: {
    title:
      'Jeg sjekket om et merke eller en leverandør var bærekraftig før jeg kjøpte noe i dag',
    description:
      'Kan logges på dagene du undersøker miljøprofilen til et produkt eller en produsent før du kjøper noe.',
    co2_explanation: `MARKDOWN:
## Fakta
- Miljømerking kan hjelpe virksomheter å produsere produkter som er tryggere og av bedre kvalitet. Det kan også hjelpe deg som forbruker å forstå leverandørkjeden og støtte bedrifter som ivaretar miljøet.
- Ved å be om mer bærekraftige produkter og vise vilje til å betale litt ekstra for bærekraftige produkter, demonstrerer du for både produsenter og politikere at bærekraft er viktig for deg og bidrar til å endre hele industrier.

## Hva vi antar
- Velger du miljømerkede produkter så ofte som mulig, antas det i beregningene at du bruker 10% mer penger på energi, mat og transport og derfor mindre på forbruk av produkter og tjenester.I tillegg antas det at du har 10% mindre miljøpåvirkning per krone brukt på produkter og tjenester. 
- Beregningene har lagt til grunn at denne aktiviteten skjer hver tredje dag. Registrer du denne aktiviteten oftere betyr det at du i realiteten har en høyere besparelse, og omvendt.
`,
    health_explanation: `MARKDOWN:
## Fakta
- Å støtte grønne initiativ, produkter og tjenester gjør at du føler deg bedre fordi du støtter et grønt skifte.
`,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_consumption_07: {
    title: 'Jeg kjøpte noe brukt i stedet for noe nytt i dag',
    description:
      'Du kan registrere denne aktiviteten når du har valgt å kjøpe noe brukt i stedet for noe nytt.',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_consumption_08: {
    title: 'Jeg sjekket vaskelappen etter mikroplast i dag',
    description:
      'Kan registeres hver dag du har sjekket vaskelappen til et klesplagg og lært om plagget har plast i seg.',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    health_explanation: `MARKDOWN:
## Fakta
- Vanlig bruksslitasje av klær fører til mikroplastutslipp som igjen dannes til husstøv.

## Tilleggsinformasjon
- [https://www.ciel.org/wp-content/uploads/2019/02/Plastic-and-Health-The-Hidden-Costs-of-a-Plastic-Planet-February-2019.pdf](https://www.ciel.org/wp-content/uploads/2019/02/Plastic-and-Health-The-Hidden-Costs-of-a-Plastic-Planet-February-2019.pdf)
- Tilleggsinformasjon om plast i klær: [https://plastjakten.no/fakta-om-plast/plast-i-klaer](https://plastjakten.no/fakta-om-plast/plast-i-klaer)`,

    biodiversity_explanation: `MARKDOWN:
## Fakta
- Alle syntetiske tekstiler er plast i fiberform som frigir mikroplast ved bruk og i vask. Mer enn 60% av tekstiler er laget av syntetiske materialer og produksjonen av slike tekstiler øker mest av all type tekstiler i dag. Det aller beste er å redusere forbruket av syntetiske fibre, men det nytter å bli bevisst på hva klærne våre er laget av og behandle dem riktig for å redusere mikroplastutslippet. En god start er dermed å sjekke merkelappen og bli kjent med hva klærne dine er laget av.

## Tilleggsinformasjon
- [https://www.hioa.no/eng/About-HiOA/Centre-for-Welfare-and-Labour-Research/SIFO/Publications-from-SIFO/Microplastic-pollution-from-textiles-A-literature-review](https://www.hioa.no/eng/About-HiOA/Centre-for-Welfare-and-Labour-Research/SIFO/Publications-from-SIFO/Microplastic-pollution-from-textiles-A-literature-review)`,
    money_explanation: `MARKDOWN:
## Fakta
- Ved å behandle klærene dine riktig tar du også bedre vare på klærne dine slik at favorittplaggene dine holder lenger. Det sparer lommeboka også på!

## Tips til klær uten plast
- [https://www.framtiden.no/gronne-tips/klar/velg-klar-uten-plast.html](https://www.framtiden.no/gronne-tips/klar/velg-klar-uten-plast.html)`,
  },
  activity_consumption_09: {
    title: 'Jeg unnlot å kjøpe nye tekstiler i dag',
    description:
      ' Kan registreres hver gang du lot være å kjøpe nye tekstiler når du egentlig ikke trengte det',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta
- I 2018 tilsvarte forbruket av nye klær i Norge hele 1 700 000 tonn CO\u2082!
- Alle klær har et miljø- og klimafotavtrykk. Selv om naturfibre er et bedre alternativ for å redusere mikroplastutslipp, kan de ha et høyere klimafotavtrykk i produksjon. Dermed er det aller beste å redusere forbruket og ta vare på det du har.

## Tilleggsinformasjon
- [https://www.framtiden.no/gronne-tips/klar/slik-blir-klarne-dine-mer-miljovennlige.html](https://www.framtiden.no/gronne-tips/klar/slik-blir-klarne-dine-mer-miljovennlige.html)`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- Alle syntetiske tekstiler er plast i fiberform som frigir mikroplast ved bruk og i vask. Mer enn 60% av tekstiler er laget av syntetiske materialer og produksjonen av slike tekstiler øker mest av all type tekstil i dag. Det aller beste er å redusere forbruket av syntetiske fibre, men det nytter å bli bevisst på hva klærne våre er laget av og behandle dem riktig for å redusere mikroplastutslippet. En god start er dermed å sjekke merkelappen og bli kjent med hva klærne dine er laget av. 
    
## Tilleggsinformasjon
- [https://www.hioa.no/eng/About-HiOA/Centre-for-Welfare-and-Labour-Research/SIFO/Publications-from-SIFO/Microplastic-pollution-from-textiles-A-literature-review](https://www.hioa.no/eng/About-HiOA/Centre-for-Welfare-and-Labour-Research/SIFO/Publications-from-SIFO/Microplastic-pollution-from-textiles-A-literature-review)`,
  },
  activity_consumption_10: {
    title:
      'Jeg unnlot å bruke kosmetikk, hud- eller hårpleieprodukter med mikroplast i dag',
    description:
      'Kan registreres hver dag du lar være å bruker kosmetikk eller hudpleieprodukter som inneholder mikroplast',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- En mengde kosmetikk er bevisst tilsatt mikroplast. Produsenter tilsetter mikroplast for å få en slipeeffekt i produkter, som bl.a. kroppskremer og skrubber. Disse mikroplastbitene havner i sluket når du dusjer eller vasker deg, videre ut i avløpet og tilslutt enten i avløpsslam spredd i naturen eller i havet. Det aller beste for både deg og miljøet er dermed å unngå å bruke sminke.

## Tilleggsinformasjon
- [https://www.beatthemicrobead.org](https://www.beatthemicrobead.org)

## Tips
- Last ned appen "Beat the Microbead" og scan dine kosmetikkprodukter etter mikroplast!
- Den mest miljøvennlige kosmetikken [https://www.framtiden.no/gronne-tips/fritid/den-mest-miljovennlige-kosmetikken.html](https://www.framtiden.no/gronne-tips/fritid/den-mest-miljovennlige-kosmetikken.html)`,
    health_explanation: `MARKDOWN:
## Fakta
- Mikroplast fungerer som en svamp for miljøgifter. Miljøgifter akkumulerer i næringskjeden og har en rekke negative helse- og miljøproblemer. Det beste for miljøet og helsa di er dermed å unngå kosmetikk og hygieneprodukter med mikroplast!`,
  },
  activity_consumption_11: {
    title:
      'Jeg sjekket kosmetikk og/eller et hygieneprodukt for mikroplast i dag',
    description:
      'Kan registeres hver dag du sjekker innholdsfortegnelsen på kosmetikk og/eller et hygieneprodukt etter mikroplast',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    health_explanation: `MARKDOWN:
## Fakta
- Mikroplast fungerer som en svamp for miljøgifter. Mikroplast tiltrekker seg andre miljøgifter. Miljøgifter akkumulerer i næringskjeden og har en rekke negative helse- og miljøproblemer. Velg gjerne Svanemerkede produkter, da de ofte er bedre alternativer.

## Tilleggsinformasjon om mikroplast i kosmetikk og hudpleieprodukter
- [https://www.beatthemicrobead.org/](https://www.beatthemicrobead.org/)`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- En mengde kosmetikk er tilsatt mikroplast. Produsenter tilsetter mikroplast for å få en slipeeffekt i produkter slik som tannkrem. Disse mikroplastbitene havner i sluket når du dusjer eller vasker deg, ut i avløpet og tilslutt enten i avløpsslam spredd i naturen eller i havet. Ved å sjekke innholdet på produktet før du kjøper og bruker det kan du redusere utslippet av mikroplast. De vanligste ingrediensene i kosmetikk som du bør unngå er: Nylon (PA), Polyethylene (PE), Polypropylene (PP), Polyethylene terephthalate (PET)

## Tips
- Last ned appen "Beat the Microbead" og scan dine kosmetikkprodukter etter mikroplast!
- [https://www.beatthemicrobead.org/guide-to-microplastics/red-list/](https://www.beatthemicrobead.org/guide-to-microplastics/red-list/)
- Den mest miljøvennlige kosmetikken [https://www.framtiden.no/gronne-tips/fritid/den-mest-miljovennlige-kosmetikken.html](https://www.framtiden.no/gronne-tips/fritid/den-mest-miljovennlige-kosmetikken.html)`,
  },
  activity_consumption_12: {
    title:
      'Jeg brukte et flergangs-hygieneprodukt, som feks barberhøvel i metall eller menskopp i stedet for et hygieneprodukt av plast i dag',
    description:
      'Kan registreres hver dag du bruker hygieneprodukter som er ikke er av plast og som kan gjenbrukes',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- 10 % av Norges befolkning kaster Q-tips i do, og mange av disse ender opp i havet og rundt omkring på strender. 
- Det er også mange andre baderomsartikler av plast som du enkelt kan redusere forbruket av. Du kan for eksempel bruke menskopp i stedet for bind og tamponger, bruke olivenolje i stedet for Q-tips av plast, eller gå over til barberhøvel for flergangsbruk. Et tips for å holde barberbladene skarpe er å dra høvelen i motsatt retning enn ved barbering over et stoff av demin!

## Tilleggsinformasjon
- [https://www.blogg.beeorganic.no/post/pusse-tenner-med-bambus-og-plantefiber](https://www.blogg.beeorganic.no/post/pusse-tenner-med-bambus-og-plantefiber)

## Tips til plastfri barbering
- [https://www.blogg.beeorganic.no/post/alt-du-trenger-%C3%A5-vite-om-plastfri-barbering](https://www.blogg.beeorganic.no/post/alt-du-trenger-%C3%A5-vite-om-plastfri-barbering)      

## Tips til hvordan man kan bli plastfri på badet
- [https://www.blogg.beeorganic.no/post/plasticfreejuly-hvordan-erstatte-plast-p%C3%A5-badet](https://www.blogg.beeorganic.no/post/plasticfreejuly-hvordan-erstatte-plast-p%C3%A5-badet)   

## Tips til plastfri mens
- [https://www.blogg.beeorganic.no/post/alt-du-trenger-%C3%A5-vite-om-milj%C3%B8vennlige-t%C3%B8ybind-og-truseinnlegg](https://www.blogg.beeorganic.no/post/alt-du-trenger-%C3%A5-vite-om-milj%C3%B8vennlige-t%C3%B8ybind-og-truseinnlegg) 

## Tips til plastfri sminkefjerning
- [https://www.blogg.beeorganic.no/post/reduser-avfall-p%C3%A5-badet-med-gjenbrukbare-bomullspads](https://www.blogg.beeorganic.no/post/reduser-avfall-p%C3%A5-badet-med-gjenbrukbare-bomullspads)`,
  },
  activity_consumption_13: {
    title:
      'Jeg laget mine egen kosmetikk og/eller andre hygieneprodukter i dag',
    description:
      'Du kan registrere, når du lager din egen kosmetikk og/eller hygieneprodukter',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    health_explanation: `MARKDOWN:
## Fakta 
- Det kan hende du smører mikroplast i ansiktet. Mange kosmetikkprodusenter tilsetter mikroplast i produkter for å få ulike egenskaper. En test gjennomført av Beat the Microbead viste at en enkelt boks med antirynkekrem inneholdt hele 1.48 millioner biter av polyethylen! Ved å lage dine egne produkter kan du redusere engangsemballasjen og være sikker på at produktet ikke inneholder mikroplast.

## Tilleggsinformasjon
- [https://www.beatthemicrobead.org](https://www.beatthemicrobead.org)         
- [https://www.framtiden.no/gronne-tips/fritid/slik-lager-du-kosmetikk-selv.html](https://www.framtiden.no/gronne-tips/fritid/slik-lager-du-kosmetikk-selv.html)

## Tips til shampo uten plast
- [https://www.blogg.beeorganic.no/post/shampo-i-fast-form-et-milj%C3%B8vennlig-alternativ](https://www.blogg.beeorganic.no/post/shampo-i-fast-form-et-milj%C3%B8vennlig-alternativ)`,
    handprint_explanation: `MARKDOWN:
## Fakta 
- Når du velger å bruke en shampo i fast form bidrar du til å redusere store mengder avfall. Tenk bare på hvor mange plastflasker du har i dusjen idag!

## Tips til shampo uten plast
- [https://www.blogg.beeorganic.no/post/shampo-i-fast-form-et-milj%C3%B8vennlig-alternativ](https://www.blogg.beeorganic.no/post/shampo-i-fast-form-et-milj%C3%B8vennlig-alternativ)`,
  },
  activity_consumption_14: {
    title: 'Jeg gjenbrukte fryseposen til andre ting i dag',
    description:
      'Kan registreres når du gjenbruker fryseposen av plast til andre ting, feks til å dekke over mat i kjøleskapet så du reduserer behovet for ny plastfolie',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta 
- Brenning av plast er den største utslippskilden til CO\u2082 fra plastens livssyklus. Plast lages i dag hovedsakelig av olje og gass. Når dette brennes frigis det CO\u2082. Ved å gjenbruke plasten man allerede har og redusere plastforbruket generelt, bidrar vi til å minimere CO\u2082-utslippet fra brenning av plast.

## Tilleggsinformasjon
- [https://klimastiftelsen.no/publikasjoner/plast-og-klima-to-sider-av-samme-sak/](https://klimastiftelsen.no/publikasjoner/plast-og-klima-to-sider-av-samme-sak/)`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- Kun rundt 5 % av avfallet blir liggende i strandsonen. Det aller meste synker til havets bunn. Plasten som blir liggende på strender blir utsatt for UV-stråler fra sola, oksygen, vær og vind og brytes dermed raskere opp til mikroplast enn plast som havner på bunnen av havet og ikke blir utsatt for oksygen og UV-stråler. Reduserer man forbruket av ny plast minimerer man også plastforsøplingen!

## Tilleggsinformasjon
- [https://ourworldindata.org/plastic-pollution](https://ourworldindata.org/plastic-pollution)

## Tips om alternativer til plastfolie
- [https://www.blogg.beeorganic.no/post/alt-du-trenger-vite-om-bivokspapir-og-plantebasert-vokspapir-et-milj%C3%B8vennlig-alternativ-til-plast](https://www.blogg.beeorganic.no/post/alt-du-trenger-vite-om-bivokspapir-og-plantebasert-vokspapir-et-milj%C3%B8vennlig-alternativ-til-plast)`,
  },
  activity_consumption_15: {
    title:
      'Jeg brukte gjenbruksbeholder eller annen alternativ oppbevaring av glass eller metall i stedet for plastbeholder i dag',
    description:
      'Kan registreres når du bruker alternative oppbevaringer du allerede har hjemme: Feks et brukt syltetøyglass eller en metallmatboks, i stedet for plast.',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta
- Årlig forbrukes det ca. 78915 tonn plastemballasje fra norske husholdninger. Ved å bruke gjenbruksbeholdere som du allerede har hjemme kan du bidra til å redusere CO\u2082 utslipp knyttet til plastproduksjon og brenning.

## Tilleggsinformasjon
- [https://www.framtiden.no/gronne-tips/fritid/slik-kan-du-redusere-engangsplasten.html](https://www.framtiden.no/gronne-tips/fritid/slik-kan-du-redusere-engangsplasten.html)

## Tilleggsinformasjon om hvordan du kan kutte engagnsplasten
- [https://www.blogg.beeorganic.no/post/plasticfreejuly-hvordan-erstatte-engangsplast](https://www.blogg.beeorganic.no/post/plasticfreejuly-hvordan-erstatte-engangsplast)`,
    health_explanation: `MARKDOWN:
## Fakta
- Noen typer matemballasje kan inneholde kreftfremkallende stoffer. For eksempel inneholder Polystyren (PS) kreftfremkallende stoffer og er utbredt i matemballasje. Å bytte ut plastemballasjen med for eksempel en matboks av rustfritt stål vil være bedre for både helsa di og miljøet.

## Tips til plastfri matbokser
- [https://www.blogg.beeorganic.no/post/milj%C3%B8vennlige-matbokser-i-metall-lag-varierte-og-sunne-matpakker-til-barna](https://www.blogg.beeorganic.no/post/milj%C3%B8vennlige-matbokser-i-metall-lag-varierte-og-sunne-matpakker-til-barna)`,
  },
  activity_consumption_16: {
    title: 'Jeg kildesorterte all plast i dag',
    description:
      'Kan registreres hver dag du kildesorterer all plast - også det som ikke er emballasje',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta
- Under 10% av all plast som er produsert siden 1950 har blitt resirkulert, og over halvparten har havnet på landfyllinger. Plast som havner på landfyllinger slipper ut CO\u2082 og metan, to sterke klimagasser. Samtidig brytes plasten opp og blir til mikroplast. Mye blir også tatt av vind og vann og havner på avveie i naturen og i havet.
- Kun 21 prosent av plastemballasjen som norske husholdninger forbrukte i 2017 ble materialgjenvunnet

## Tilleggsinformasjon om Plastpilene
- [https://www.framtiden.no/merkeguiden/elektronikk/plastpiler.html](https://www.framtiden.no/merkeguiden/elektronikk/plastpiler.html)`,
    handprint_explanation: `MARKDOWN:
## Fakta
- Det er kun *plastemballasje* som skal i innsamlingsordningen i Norge. Dette fordi det er emballasjeprodusentene som har avtale med myndighetene og finansierer de ulike returordningene. De betaler for at emballasjen blir samlet inn og sendt til materialgjenvinning. Tilsvarende ordning finnes ikke for produkter laget av samme materiale (eks plastleker, akebrett osv). 
- Plastavfallet skal være rent og drypptørt når det resirkuleres, men husk å skylle i kaldt vann så ikke vinninga går opp i spinninga. Kildesortering kan variere over hele landet. Det er fordi det er kommunene som bestemmer hvilke kildesorteringsordninger de skal ha. Er du ikke fornøyd med din kommune har du rett til å si ifra!

## Tilleggsinformasjon
- [https://sortere.no/](https://sortere.no/)

## Tips til hvordan du kildesorterer riktig
- [https://sortere.no/slikgjordudet](https://sortere.no/slikgjordudet)`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- Bioplast sorteres som plastemballasje, mens nedbrytbar og komposterbar plast skal i restavfallet. Nedbrytbar og komposterbar plast vil ødelegge kvaliteten på produkter som lages av gjenvunnet plast. Emballasjen er oftest merket dersom den er en av disse typene. Navnene til disse nye plasttypene kan gi inntrykk av at de kan kastes og brytes ned i naturen. Det stemmer ikke. Både bioplast og nedbrytbar plast blir til mikroplast når den havner i naturen.

## Tilleggsinformasjon
- [https://phys.org/news/2017-12-truth-bioplastics.html](https://phys.org/news/2017-12-truth-bioplastics.html)
- [https://www.grontpunkt.no/nyhet/hva-er-egentlig-bioplast/](https://www.grontpunkt.no/nyhet/hva-er-egentlig-bioplast/)`,
    money_explanation: `MARKDOWN:
## Fakta
- Plast som blir kastet har enorm verdi. En rapport av World Economic Forum, Ellen Macarthur Foundation og McKinsey & Company fra 2016 viste at verdien av all plast som ble kastet lå på rundt 80-120 milliarder amerikanske dollar årlig!

## Tilleggsinformasjon
- [https://www.ellenmacarthurfoundation.org/publications/the-new-plastics-economy-rethinking-the-future-of-plastics](https://www.ellenmacarthurfoundation.org/publications/the-new-plastics-economy-rethinking-the-future-of-plastics)`,
  },
  activity_consumption_17: {
    title:
      'Jeg tok med og brukte mitt eget gjenbruksbestikk i stedet for engangsbestikk av plast i dag',
    description:
      'Registreres hver dag du tar med deg og bruker bestikk hjemmefra så du slipper engangsbestikk av plast!',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta
- CO\u2082-utslipp fra plastens verdikjede øker. Dette skyldes i stor grad en enorm økning i forbruk av plastartikler og spesielt engangsartikler.  Hvis vi fortsetter med den samme økningen som nå, kan utslippene fra plastens verdikjede stå for 20% av alle CO\u2082 utslipp i 2050.

## Tilleggsinformasjon
- [https://klimastiftelsen.no/wp-content/uploads/2018/09/Plast-og-klima-web.pdf](https://klimastiftelsen.no/wp-content/uploads/2018/09/Plast-og-klima-web.pdf)`,
    health_explanation: `MARKDOWN:
## Fakta 
- Noen typer matemballasje kan inneholde kreftfremkallende stoffer. For eksempel inneholder Polystyren (PS) kreftfremkallende stoffer og er utbredt i matemballasje. Å bytte ut plastemballasjen med for eksempel en matboks av rustfritt stål vil være bedre for både helsa di og miljøet.

## Tilleggsinformasjon
- [https://www.ciel.org/wp-content/uploads/2019/02/Plastic-and-Health-The-Hidden-Costs-of-a-Plastic-Planet-February-2019.pdf](https://www.ciel.org/wp-content/uploads/2019/02/Plastic-and-Health-The-Hidden-Costs-of-a-Plastic-Planet-February-2019.pdf)`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- Plastens største marked er emballasje. Over 40 % av all plast produsert er emballasje. Emballasje er også den produktgruppen med lavest levetid, da mye består av engangsartikler. Mye av emballasjen havner dessverre på avveie og ender opp i naturen og i havet.

## Tilleggsinformasjon
- [https://www.ellenmacarthurfoundation.org/assets/downloads/New-Plastics-Economy_Catalysing-Action_13-1-17.pdf](https://www.ellenmacarthurfoundation.org/assets/downloads/New-Plastics-Economy_Catalysing-Action_13-1-17.pdf)`,
  },
  activity_consumption_18: {
    title:
      'Jeg brukte flerbruksflaske i stedet for å kjøpe engangsflaske i dag',
    description:
      'Kan registreres hver gang du bruker gjenbruksflaske av for eksempel stål eller glass i stedet for flaske av plast',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta 
- Mange plastflasker blir ikke pantet i Norge, selv om vi har et fantastisk pantesystem. Tall fra Infinitum AS, som drifter panteordningen, viser at mer enn 82 millioner plastflasker ikke blir pantet, men enten havner i restavfall der de blir brent, eller i naturen. Med tanke på at flaskene er laget av fossile råvarer, kan man bidra mye med å bruke gjenbruksflaske i stedet for å kjøpe flaskevann. For hver eneste flaske som pantes og blir materialgjenvunnet, sammenlignet med om flasken blir kastet i restavfallet og energigjenvunnet, sparer man 0,178 kg CO\u2082. Tenk så mye CO\u2082 vi kunne spart hvis alle de 82 millioner plastflaskene hadde blitt pantet i stedet for å bli kastet i restavfallet!`,
    health_explanation: `MARKDOWN:
## Fakta
- Det er gjort tester som viser at drikkeflasker av plast lekker helseskadelige stoffer slik som ftalater, bisfenol A (BPA), bly med flere. Det beste for både helsa di og miljøet er dermed å gå over til drikkeflasker av glass eller metall.

## Tilleggsinformasjon om helseskadelige stoffer i plastflasker
- [https://www.forbrukerradet.no/siste-nytt/drikkeflasker-lekker-kjemikalier/](https://www.forbrukerradet.no/siste-nytt/drikkeflasker-lekker-kjemikalier/)
`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- Plast brytes ikke ned. Det er mer riktig å si at det brytes opp. Når plast blir liggende i naturen eller på landfyllinger, brytes det sakte opp til mindre og mindre biter – først til mikroplast, deretter til nanoplast og blir så lite at man ikke kan se det uten mikroskop. Men plast gjennomgår ikke en fullstendig nedbryting. Siden plasten kan brytes opp til så små biter, er det manglende kunnskap og forskning på hva som egentlig skjer med plasten. Hvor lang tid det tar for naturen å bryte opp plast avhenger av typen plast produktet er laget av, hvilke tilsetningsstoffer det har i seg og hvor det ligger. Ifølge Miljøstatus kan det ta mellom 1-5 år å bryte ned en sigarettsneip, 400 år å bryte ned drikkebeholder i plast og 50 år å bryte ned en isoporkopp. 

## Tilleggsinformasjon
- [https://miljostatus.miljodirektoratet.no/tema/avfall/avfallstyper/mikroplast/](https://miljostatus.miljodirektoratet.no/tema/avfall/avfallstyper/mikroplast/)`,
  },
  activity_consumption_19: {
    title:
      'Jeg brukte et av mine handlenett i stedet for å kjøpe plastpose i dag',
    description:
      'Registreres hver dag du bruker et handlenett du allerede hadde i stedet for å kjøpe en ny plastpose',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta
- Det kreves ca. 2 kilo olje for å produsere 1 kilo plast. I tillegg kommer energi knyttet til produksjon, transport og forbrenning. Vi vet at de fleste plastposer i Norge blir brent, og det er ved brenning at plasten avgir mest CO\u2082 gjennom hele verdikjeden. Jo flere ganger du bruker en plastpose før den blir til avfall jo bedre. Enda bedre er det om du bruker noe du allerede har, som for eksempel et handlenett. Men husk at det ikke hjelper hvis du kjøper nytt handlenett hver gang du skal handle, da de også krever mye ressurser å produsere.

## Tips
- Plastpose eller papirpose? [https://www.framtiden.no/gronne-tips/spor-oss/er-papirposer-verre-for-miljoet-enn-plastposer.html](https://www.framtiden.no/gronne-tips/spor-oss/er-papirposer-verre-for-miljoet-enn-plastposer.html)`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- Vi bruker over 5 billioner plastposer årlig verden over. Det tilsvarer mer enn 160.000 poser i minuttet. I Norge brennes de aller fleste plastposene, men i mange andre land er plastposer på avveie et stort miljøproblem og kan skade både natur og dyr. Plastposer som havner i havet kan bli mistolket som en manet og dermed bli spist av større fisk og havdyr. `,
    money_explanation: `MARKDOWN:
## Fakta
- Vi forbruker mer enn 800 millioner plastbæreposer årlig, bare i Norge? Siden du må betale for handleposen, sparer du noen kroner ved å ta med egne handlenett på butikken i stedet.`,
    handprint_explanation: `MARKDOWN:
## Fakta 
- Som forbruker har du mye påvirkningskraft. Gjennom handlignene du gjør - feks å ta med handlenett og si nei takk til plastpose - påvirker du både næringslivet og de rundt deg!`,
  },
  activity_consumption_20: {
    title: 'Jeg sa nei takk til unødvendig plast i dag',
    description:
      'Du kan registrere, når du sier nei takk til produkter av plast som du kan klare deg uten',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- Det er de rikeste landene som genererer mest plastavfall per person. På grunn av dårligere avfallshåndtering er det allikevel lavinntektsland som står som de verste plastforsøplerne. Plasten som havner på avveie finner ofte veien til vannveier og via vind til elver som igjen renner ut i havet.       
- En av de vanligste tingene å finne på norske strender er uidentifiserbare plastbiter. Dette kan være plastbiter fra alt mulig rart som har blitt brutt opp og ødelagt av vær og vind. Å si nei til unødvendig engangsplast er et stort steg i riktig retning for å redusere dette problemet.

## Tilleggsinformasjon
- [https://europa.eu/rapid/press-release_IP-18-3927_en.htm](https://europa.eu/rapid/press-release_IP-18-3927_en.htm)`,
    handprint_explanation: `MARKDOWN:
## Fakta
Produkter av plast blir ikke produsert med mindre noen etterspør det. Dermed kan du påvirke stort ved å si nei takk til plastprodukter som er helt unødvendige.`,
  },
  activity_consumption_21: {
    title: 'Jeg hadde et plastemballasjefritt måltid i dag',
    description:
      'Kan registreres hver dag du lager middag eller et annet måltid av matvarer uten plastemballasje og uten å etterlate deg plastavfall',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta
- En europeisk innbygger kaster i snitt  mer enn 30 kg plastemballasje hvert år. Det aller meste av dette er engangsemballasje der lite blir resirkulert og mye blir brent eller havner på deponier og dermed bidrar enten til forsøpling eller CO\u2082 utslipp.`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- Multipakker kan føre til økt matsvinn. Store forpakninger fører til at mange kjøper inn mer enn de trenger og får spist opp. Samtidig er det pakket inn i plast og bidrar til økt emballasjebruk. En del varer er også dobbeltemballert med unødvendig bruk av ekstra plast. Prøv å unngå unødvendig plastbruk på matvarer som fint holder seg uten plastemballasje, og velg løsvekt der du kan både for å redusere matsvinn og plast.

## Tilleggsinformasjon
- [https://www.foeeurope.org/unwrapped-throwaway-plastic-food-waste](https://www.foeeurope.org/unwrapped-throwaway-plastic-food-waste)

## Tips for å redusere matsvinn
- [https://www.framtiden.no/gronne-tips/mat/matvettreglene.html](https://www.framtiden.no/gronne-tips/mat/matvettreglene.html)`,
  },
  activity_consumption_22: {
    title: 'Jeg valgte matvareprodukter uten plastemballasje i dag ',
    description:
      'Kan registreres når du handler varer i løsvekt og bruker egne gjenbruksbeholdere i for eksempel glass eller stål, eller frukt- og grønt nett, framfor plastemballasje.',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta
- Matsvinn står for 8 % av alle menneskeskapte klimagassutslipp. Hadde matsvinn vært et land ville bare Kina og USA hatt høyrere utslipp.

## Tips til en plastfri handletur
- [https://www.beeco.no/2017/03/tips-til-handle-mat-med-minst-mulig.html](https://www.beeco.no/2017/03/tips-til-handle-mat-med-minst-mulig.html)`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- Plastemballasje rundt noen typer mat kan redusere matsvinn og opprettholde kvaliteten på produktet. Svinnet på druer har for eksempel gått ned med 75 % etter at det ble pakket i plastpakker, ifølge Bama.Samtidig viser en rapport gjennomført av Friends of The Earth Europe at matsvinn har økt i takt med plastemballasjebruk. Det er med andre ord mye unødvendig plast rundt en del matvarer. Mange produkter selges heldigvis i løsvekt. Det kan derfor i noen tilfeller være bra for klimaet om du velger noen spesielt sårbare matvarer pakket i emballasje, hvis du sørger for at det kildesorteres riktig. Men at du som hovedregel tar med deg et handlenett og dropper små plastposer og plastemballasje når du er på butikken.

## Tilleggsinformasjon
- [https://nofima.no/wp-content/uploads/2016/10/BAMAs-milj%C3%B8m%C3%A5l-nr-1-Redusert-matsvinn-Sylvia-Lofthus.pdf](https://nofima.no/wp-content/uploads/2016/10/BAMAs-milj%C3%B8m%C3%A5l-nr-1-Redusert-matsvinn-Sylvia-Lofthus.pdf)`,
    handprint_explanation: `MARKDOWN:
## Fakta
- Du kan være med på å skape nye trender! Det føles kanskje litt rart å ta med seg egen glasskrukke eller hekla nett på butikken, men jo flere som gjør dette jo mer normalisert blir det!`,
  },
  activity_consumption_23: {
    title:
      'Jeg reparerte et produkt av/med plast framfor å kjøpe et nytt produkt av/med plast i dag',
    description:
      'Kan registreres når du reparerer et produkt av eller med plast i stedet for å kjøpe et nytt. Kanskje du har en favoritt treningsbukse du kan reparere?',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta
- Vårt private forbruk står for litt over 70% av de menneskeskapte klimagassutslippene globalt. Ting og tang står for 13% av utslippene. I tillegg er veldig mye av det vi har i hjemmene våre laget av eller med plast. Ved å reparere det du allerede har, kan du dermed bidra til å redusere plastavfall og klimagassutslipp!

## Tilleggsinformasjon
- [https://www.framtiden.no/gronne-tips/fritid/21-klimafotavtrykk-av-forbruket-vart.html](https://www.framtiden.no/gronne-tips/fritid/21-klimafotavtrykk-av-forbruket-vart.html)

## Tips om merker som tilbyr reparasjon
- [https://www.framtiden.no/gronne-tips/klar/syv-merker-som-tilbyr-reparasjon.html](https://www.framtiden.no/gronne-tips/klar/syv-merker-som-tilbyr-reparasjon.html)

## Tips til å reparere mobilen
- [https://tavarepadetduhar.no/fritid/slik-kan-du-fikse-mobilen-din-article264-818.html](https://tavarepadetduhar.no/fritid/slik-kan-du-fikse-mobilen-din-article264-818.html)

## Tips til å reparere ting du har hjemme
- [https://tavarepadetduhar.no/klar-tilbehor/](https://tavarepadetduhar.no/klar-tilbehor/)`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- Plast er ett av de vanligste materialene man finner i mobiltelefoner. Ved å reparere den telefonen du allerede har bidrar du til redusert plastavfall, redusert plastforbruk og klimagassutslipp tilknyttet produksjon, transport og avfallshåndtering. Andre forbruksvarer som inneholder plast er for eksempel laptop, klær, møbeltrekk og andre tekstiler, leker, hagemøbler og mye mer.

## Tips til å reparere mobilen
- [https://tavarepadetduhar.no/fritid/slik-kan-du-fikse-mobilen-din-article264-818.html](https://tavarepadetduhar.no/fritid/slik-kan-du-fikse-mobilen-din-article264-818.html)

## Tips til å reparere ting du har hjemme
- [https://tavarepadetduhar.no/klar-tilbehor/](https://tavarepadetduhar.no/klar-tilbehor/)`,
  },
  activity_consumption_24: {
    title:
      'Jeg leide, lånte eller kjøpte et brukt produkt av/med plast i stedet for å kjøpe det nytt i dag',
    description:
      'Du kan registrere, når du har leid, kjøpt brukt, byttet eller arvet ett eller flere produkter av/med plast i stedet for å kjøpe deg et nytt produkt',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- Plast varer nesten evig. Den eneste måten å permanent ødelegge eller fjerne plast fra jorda på er ved hjelp av ødeleggende termisk behandling slik som forbrenning eller pyrolyse. Ingen av de vanligste plasttypene er biologisk nedbrytbare og akkumuleres dermed på landfyllinger eller i naturen. Derfor er det aller beste å unngå å produsere mer plast og å få den plasten som finnes over i kretsløpsøkonomien der vi tar vare på det vi allerede har og kjøper brukt der vi kan.

## Tilleggsinformasjon
- [https://www.framtiden.no/aktuelle-rapporter/838-et-hav-av-plast/file.html](https://www.framtiden.no/aktuelle-rapporter/838-et-hav-av-plast/file.html)`,
    co2_explanation: `MARKDOWN:
## Fakta
- Det kreves ca. 2 kilo olje for å produsere 1 kilo plast. I tillegg kommer energi knyttet til produksjon, transport og forbrenning. Det betyr at hver gang du kjøper noe brukt i stedet for nytt, bidrar du til å forhindre nye CO\u2082 utslipp!

## Tips
- Bruk Finn.no og andre bruktmarkeder til å finne det du trenger brukt!`,
  },
  activity_consumption_25: {
    title: 'Jeg brukte flerbrukskopp i stedet for å kjøpe engangkopp i dag',
    description:
      'Registrer hver dag du har med deg gjenbrukskopp i stedet for å kjøpe/bruke engangskopp',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    biodiversity_explanation: `MARKDOWN:
## Fakta 
- Plastkopper og -lokk kommer til å bli forbudt i Norge fra 2020 og i EU fra 2021? Like greit å være tidlig ute med å endre vanene til å ha med seg termokopp i sekken!
    
## Tilleggsinformasjon om forbudet mot engangsplast
- [https://www.regjeringen.no/no/aktuelt/regjeringen-varsler-forbud-mot-engangsartikler-av-plast-innen-ett-ar/id2643870/](https://www.regjeringen.no/no/aktuelt/regjeringen-varsler-forbud-mot-engangsartikler-av-plast-innen-ett-ar/id2643870/)
- [https://rethinkplasticalliance.eu/wp-content/uploads/2019/05/ZWE_Unfolding-the-SUP-directive.pdf](https://rethinkplasticalliance.eu/wp-content/uploads/2019/05/ZWE_Unfolding-the-SUP-directive.pdf)`,
  },
  activity_consumption_26: {
    title: 'Jeg brukte en gjenbrukspose da jeg var på shopping i dag',
    description:
      'Kan logges på dager du tok med deg en gjenbrukspose mens du gikk på shopping i stedet for å kjøpe en plastpose',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta
- Miljøbekymringene knyttet til plastbæreposer inkluderer bruk av ikke-fornybare ressurser til produksjon (som råolje), kostnaden ved å behandle plasten når det kastes, og effekten av forsøpling.
- Det er to hovedtyper handleposer - engangs- og flerbruksposer. Forskning har vist at flerbruksposer er miljømessig overlegne engangsbæreposer bare hvis de brukes mange ganger. Ifølge en studie fra det danske miljøtilsynet bør flerbruksposer av polypropen gjenbrukes minst 52 ganger for å oppnå miljøgevinsten ved å bytte fra engangsposer. Dette understreker viktigheten av å gjenbruke flerbruksposer.

## Hva vi antar
- Vi estimerer gjennomsnittsvekten av plastbæreposer brukt av en person i løpet av et år for et respektive land og kombinerer dette tallet med CO\u2082e-utslippsfaktoren for LDPE-poser for å beregne besparelsene fra denne aktiviteten`,
  },
  activity_consumption_27: {
    title: 'Jeg brukte ikke engangsbeholdere som plastflasker i dag',
    description:
      'Kan logges på dager du avstod fra å kjøpe vannflasker i plast',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta
- Produksjon av plastflasker er ressurskrevende og har store miljøpåvirkninger. Produksjonsprosessen krever opptil 17 millioner fat olje hvert år bare for å møte USAs årlige etterspørsel etter flaskevann. Videre krever produksjonen av en vannflaske tre ganger så mye vann for å fylle den.
- Rundt 8 millioner tonn plast ender i havene hvert år, og dreper mer enn 1,1 millioner sjøfugler og dyr årlig. Kasserte flasker og tilhørende korker utgjør det tredje og fjerde mest gjenvunnede plastavfallet
- Gjennomsnittspersonen i Storbritannia bruker 150 plastvannflasker per år. Gjenvinningsgraden for plastflasker i Storbritannia er rundt 57 %, noe som betyr at nesten halvparten av flaskene enten kastes, deponeres eller forbrennes. I følge avfallshierarkiet, som rangerer avfallshåndteringsmetoder etter deres påvirkning på miljøet, er forbrenning den nest verste avfallshåndteringsprosessen og deponi den verste. Så å redusere bruken av plastflasker er det beste for bærekraft!

## Hva vi antar
- Vi estimerer gjennomsnittsvekten av plastflasker brukt av en person i løpet av et år for et respektive land og kombinerer dette tallet med CO\u2082e-utslippsfaktoren for PET-flasker for å beregne besparelsene fra denne aktiviteten`,
  },
  activity_consumption_28: {
    title: 'Jeg har ikke skrevet ut unødvendig papir i dag',
    description:
      'Kan logges på dager du aktivt forsøkte å redusere papirbruken',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta
- På verdensbasis blir 4 milliarder trær hugget ned hvert år for papir. Det er én prosent av Amazonas regnskog - hvert eneste år.
- Det trengs rundt 10 liter vann, kun for å lage ett ark A4-papir.

## Hva vi antar
- Vi estimerer gjennomsnittlig vekt på papir brukt av en person i løpet av et år for et respektive land og kombinerer dette tallet med CO\u2082e-utslippsfaktoren for ikke-belagt papir for å beregne besparelsene fra denne aktiviteten`,
  },
  activity_consumption_29: {
    title:
      'Jeg valgte å ikke kjøpe nye klær i dag og kjøpte brukte klær i stedet',
    description:
      'Kan logges på dager du bruker rundt 100 NOK på brukte klær, i stedet for å kjøpe nye klær.',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Hva vi antar
- Vi antar at klærne du kjøper erstatter produksjonen av nytt tøy for samme mengde penger som du brukte,
- Klimagassutslippet beregnes basert på en gjennomsnittlig CO\u2082e utslipp per krone for klær som selges i Norge.

## Tilleggsinformasjon
- [Vi kjøper alt for mange klær](https://www.framtiden.no/202008207610/aktuelt/forbruk/vi-kjoper-altfor-mange-klar.html).
`,
  },
  activity_consumption_30: {
    title: 'Jeg handlet brukte klær for mye penger i dag, fremfor å kjøpe nytt',
    description:
      'Kan logges på dager du bruker rundt 500 NOK på brukte klær, i stedet for å kjøpe nye klær.',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta 
- Moteindustrier produserer ca [10% av globale klimagassutslipp, og 73% av tøy som avlegges ender opp med å bli brent eller på søppelfylling](https://www.ellenmacarthurfoundation.org/assets/downloads/publications/A-New-Textiles-Economy_Summary-of-Findings_Updated_1-12-17.pdf). Ved å kjøpe brukt, erstatter du produksjonen av nytt tøy samtidig som du oppdateter garderoben din.

## Hva vi antar
- Vi antar at klærne du kjøper erstatter produksjonen av nytt tøy for samme mengde penger som du brukte,
- Klimagassutslippet beregnes basert på en gjennomsnittlig CO\u2082e utslipp per krone for klær som selges i Norge.

## Tilleggsinformasjon
- [Vi kjøper alt for mange klær](https://www.framtiden.no/202008207610/aktuelt/forbruk/vi-kjoper-altfor-mange-klar.html).
`,
  },
  activity_consumption_31: {
    title:
      'Jeg valgte å ikke kjøpe ny elektronikk i dag og kjøpte brukt elektronikk i stedet',
    description:
      'Kan logges på dager du bruker rundt 500 NOK på brukte elektroniske gjenstander, i stedet for å kjøpe ny elektronikk.',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta 
- Nordmenn genererer mest el-avfall per person av alle i Europa, til sammen nesten 29 kg/år.

## Hva vi antar
- Vi antar at kjøp av brukt elektronikk erstatter behovet for å kjøpe ny elektronikk til tilsvarende beløp
- Klimagassutslippet beregnes basert på en gjennomsnittlig CO\u2082e utslipp per krone for klær som selges i Norge.

## Tilleggsinformasjon
- [Tips om å kjøpe brukt elektronikk](https://framtiden.no/gronne-tips/data-og-elektronikk/tips-til-kjop-av-brukt-elektronikk.html).
- [Tilleggsinformasjon om fotavtrykk til forbrukerelektronikk](https://eeb.org/wp-content/uploads/2019/09/Coolproducts-report.pdf).
`,
  },
  activity_consumption_32: {
    title:
      'Jeg handlet brukt elektronikk for mye penger i dag, fremfor å kjøpe nytt',
    description:
      'Kan logges på dager du bruker rundt 1000 kroner på brukte elektroniske gjenstander, i stedet for å kjøpe ny elektronikk.',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta 
- Nordmenn genererer mest el-avfall per person av alle i Europa, til sammen nesten 29 kg/år.

## Hva vi antar
- Vi antar at kjøp av brukt elektronikk erstatter behovet for å kjøpe ny elektronikk til tilsvarende beløp
- Klimagassutslippet beregnes basert på en gjennomsnittlig CO\u2082e utslipp per krone for klær som selges i Norge.

## Tilleggsinformasjon
- [Tips om å kjøpe brukt elektronikk](https://framtiden.no/gronne-tips/data-og-elektronikk/tips-til-kjop-av-brukt-elektronikk.html).
- [Tilleggsinformasjon om fotavtrykk til forbrukerelektronikk](https://mk0eeborgicuypctuf7e.kinstacdn.com/wp-content/uploads/2019/09/Coolproducts-report.pdf).
`,
  },
  activity_consumption_33: {
    title:
      'Jeg valgte å ikke kjøpe noe nytt i dag og kjøpte i stedet noe brukt',
    description:
      'Kan logges på dager du kjøper en brukt vare (annet enn klær eller elektronikk) som koster rundt 100 NOK, i stedet for å kjøpe noe nytt.',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta 
- Alt du kjøper krever materialer og energi å produsere, og har derfor et fotavtrykk.
- Du kan bidra til å skape framtidens sirkulær økonomi ved å forlenge livet til produkter i brukt.

## Hva vi antar
- Vi antar av det du kjøper brukte erstatter samme sum forbruk på nye gjenstander.
- Besparelsen beregnes basert på hvor mye CO\u2082 gjennomsnittskronen brukt på produkter generer i den norske økonomien.
`,
  },
  activity_consumption_34: {
    title: 'Jeg handlet noe brukt for mye penger i dag, fremfor å kjøpe nytt',
    description:
      'Kan logges på dager du kjøper en annen brukt vare enn klær eller elektronikk som koster rundt 1000 NOK, i stedet for å kjøpe noe nytt.',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta 
- Alt du kjøper krever materialer og energi å produsere, og har derfor et fotavtrykk.
- Du kan bidra til å skape framtidens sirkulær økonomi ved å forlenge livet til produkter i brukt.

## Hva vi antar
- Vi antar av det du kjøper brukte erstatter samme sum forbruk på nye gjenstander.
- Besparelsen beregnes basert på hvor mye CO\u2082 gjennomsnittskronen brukt på produkter generer i den norske økonomien.
`,
  },
  activity_consumption_35: {
    title: 'Jeg kjøpte ingen varer i dag',
    description:
      'Kan logges på dager der du ikke brukte penger på å kjøpe ikke-essensielle produkter eller gjenstander som møbler, pyntegjenstander, klær eller andre personlige kjøp.',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta 
- Alle kjøp har et fotavtrykk, fordi det krever ressurser å produsere og levere varer og tjenester. Ved å ikke kjøpe noe, reduseres derfor ditt forbruk og fotavtrykk.
- Klimavennlig atferd henger tett sammen med våre kjøpsvaner. Men siden pengene vi sparer sjeldent blir spart i ubestemt tid, har denne aktiviteten vært litt utfordrende å lage. For selv om vi ikke kjøper noe i dag, betyr ikke det at pengene ikke blir brukt i morgen - på noe som kanskje er værre for miljøet. Dette er kjent som [rebound-effekten](https://www.futurelearn.com/info/courses/sustainability-society-and-you/0/steps/4633)

## Hva vi antar
- Vi antar i beregningen at de pengene som spares på å ikke kjøpe noen ikke-essensielle produkter denne dagen, ikke brukes til å kjøpe ikke-essensielle produkter etterpå. Basert på dette antar vi at ditt forbruk blir lavere enn en gjennomsnittsperson sitt. 
- Vi beregner det daglige fotavtrykket knyttet til kjøp av varer, og bruker dette til å anslå gjennomsnittsbesparelsen fra denne aktiviteten. 
`,
  },
};
